import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Collection} from '@tinct/web-collection';
import {useUserOrganizationsColumns} from './hooks/use-user-organizations-columns';
import {useGetUserOrganizations} from '@tinct/core-api';
import {LayoutContent, LayoutHeader, LayoutTopbar} from '@tinct/web-layout';
import {useConnectedUser} from '@tinct/core-store-session';
import {Organization, User} from '@tinct/core-api-models';
import {UpdateOrganizationModal} from './modals/update-organization-modal';
import {DeleteOrganizationModal} from './modals/delete-organization-modal';
import {CreateUserOrganizationModal} from '../../modals/create-user-organization-modal';
import {Button} from "@tinct/web-components";
import {Plus} from "lucide-react";

export function ListUserOrganizationsPage() {
  const navigate = useNavigate();
  const connectedUser = useConnectedUser() as User;
  const userId = connectedUser?.id;

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  const columns = useUserOrganizationsColumns({
    onManageWorkspace: (organization: Organization) => {
      if (organization.id) {
        navigate(`/w/${organization.id}/settings`);
      }
    },
    onRenameWorkspace: (organization: Organization) => {
      setSelectedOrganization(organization);
      setRenameModalOpen(true);
    },
    onDeleteWorkspace: (organization: Organization) => {
      setSelectedOrganization(organization);
      setDeleteModalOpen(true);
    },
  });

  if (!userId) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar />
      </header>
      <LayoutContent>
        <LayoutHeader
          pageTitle="Workspaces"
          pageDescription="Manage all your workspaces"
        />
        <div className="flex flex-col gap-6">
          <Collection
            useLocationAsState
            columns={columns}
            filtersConfiguration={{
              textFilter: {
                placeholder: 'Search workspaces by name',
                queryParamName: 'term',
              }
            }}
            toolbarActions={[
              <Button key='create-workspace' onClick={() => setCreateModalOpen(true)} className="cursor-pointer">
                <Plus className="h-4 w-4" />
                Create new workspace
              </Button>
            ]}
            useQueryFn={useGetUserOrganizations}
            staticParams={{ userId }}
            queryFields={{
              id: null,
              name: null,
              type: null,
              parent: {
                fields: {
                  id: null,
                  name: null,
                }
              },
              createdAt: null,
              updatedAt: null,
              deleteAt: null,
              status: null,
              subscription: null,
              statistics: {
                fields: {
                  publishedCampaignsCount: null,
                  activeMembersCount: null,
                }
              }
            }}
          />
        </div>
      </LayoutContent>

      <CreateUserOrganizationModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        userId={userId}
      />

      {selectedOrganization && (
        <>
          <UpdateOrganizationModal
            open={renameModalOpen}
            onOpenChange={setRenameModalOpen}
            organization={selectedOrganization}
          />
          <DeleteOrganizationModal
            open={deleteModalOpen}
            onOpenChange={setDeleteModalOpen}
            organization={selectedOrganization}
          />
        </>
      )}
    </div>
  );
}
